/**
 * Where things sit on the Activity Map CANVAS (the Overview home's map view on the desktop) — the web-only
 * re-layout of the shared world. `map-layout.ts` is mirrored 1:1 by both phones and pinned by their parity
 * tests, so the canvas never edits it: it moves nodes AFTER the shared build.
 *
 * Every number here is read off the canvas frame (Figma 2038:96143, the 1920x1080 home) and converted into
 * world units with the agents ring as the ruler: the frame draws the ring at a 167.655px radius (its 100%
 * zoom) where the world draws it at `AGENT_RING_R` = 120 — which is also what the default view renders, so
 * the frame and the world agree at rest.
 *
 * Pure and import-free (bar the shared constants), so it is unit-testable.
 */

import { AGENT_RING_R, WORLD_CENTER, type MapNode } from './map-layout';

/** World units per Figma px of the canvas frame — the ring's 120 over the frame's 167.655 (its 335.31 circle). */
export const CANVAS_FIGMA_SCALE = AGENT_RING_R / 167.655;

/** The Maestro's centre in the canvas frame — the origin every frame point is measured from. */
const FRAME_MAESTRO = { x: 1234.84, y: 453.04 };

const fromFigma = (dx: number, dy: number) => ({ dx: dx * CANVAS_FIGMA_SCALE, dy: dy * CANVAS_FIGMA_SCALE });
/** A frame point → its offset from the Maestro, in world units. */
const atFigma = (fx: number, fy: number) => fromFigma(fx - FRAME_MAESTRO.x, fy - FRAME_MAESTRO.y);

/**
 * A channel plate: the frame's 50px circle and its glyph — the frame's icons sit in a 20.442 box, the SMS
 * one in 22.144, WhatsApp's own vector at 18.215 — each stroked 1.5 at its size.
 */
export const CANVAS_CHANNEL = {
  r: 25 * CANVAS_FIGMA_SCALE,
  glyph: 20.442 * CANVAS_FIGMA_SCALE,
  glyphBySlug: { 'sms-in': 22.144 * CANVAS_FIGMA_SCALE } as Partial<Record<string, number>>,
  whatsapp: 18.215 * CANVAS_FIGMA_SCALE,
  /** Lucide draws in a 24 box, so the frame's 1.5 at a glyph's size is this in the icon's own units. */
  strokeWidthAt: (glyph: number) => (1.5 * 24) / (glyph / CANVAS_FIGMA_SCALE),
} as const;

/**
 * The channels-in column, by KIND — each slot is the plate's centre as an offset from the Maestro, measured
 * on the frame (plates' centres below). The five the frame draws keep their own places whatever else the
 * account has connected, so a channel never jumps because another one appeared.
 */
const CHANNEL_SLOTS: Record<string, { dx: number; dy: number }> = {
  'phone-app': atFigma(868.382, 257.334),
  web: atFigma(922.07, 400.311),
  'email-in': atFigma(791, 516.33),
  whatsapp: atFigma(969.983, 570.744),
  'sms-in': atFigma(921.689, 653.757),
};

/**
 * Every OTHER channel (an AI-coder client, the folded "+N"), in order. The frame draws five; these continue
 * its zig-zag into the gaps it leaves — the first between the phone and the web plates, further out, which
 * is the widest gap in the column. Not in the frame: placed here, to be moved when design draws them.
 * Three is the most the shared fold can produce beside the five (`MAX_SIDE_NODES` = 8).
 */
const EXTRA_SLOTS: { dx: number; dy: number }[] = [fromFigma(-430, -120), fromFigma(-420, 190), fromFigma(-440, -260)];

/**
 * The channels-in nodes moved to their canvas slots. Nodes of any other region pass through untouched. A
 * channel past the extra slots keeps its shared position rather than being dropped.
 */
export function placeCanvasChannels(nodes: MapNode[]): MapNode[] {
  let extra = 0;
  return nodes.map((node) => {
    if (node.region !== 'channelsIn') return node;
    const slot = (!node.folded && node.slug ? CHANNEL_SLOTS[node.slug] : undefined) ?? EXTRA_SLOTS[extra++];
    return slot ? { ...node, x: WORLD_CENTER.x + slot.dx, y: WORLD_CENTER.y + slot.dy } : node;
  });
}

// ---------------------------------------------------------------------------------------------------------
// The canvas CONNECTORS row (Figma 2038:96143)
// ---------------------------------------------------------------------------------------------------------

/**
 * The connected tools in ONE tight row under the ring: 50px plates 2 apart, centred where the frame centres
 * its five (1252.35 · 770.752 — a touch right of and well below the Maestro). The account's own count goes in
 * the same row, growing equally both ways about that centre. The trailing "+ connect" node is the row's last
 * plate, as the frame draws it — the one plate with a hairline.
 */
export const CANVAS_CONNECTORS = {
  centre: atFigma(1252.35, 770.752),
  /** One plate's step along the row: its 50 plus the 2 gap. */
  step: 52 * CANVAS_FIGMA_SCALE,
  /** The plate's own radius — the same 50 circle as a channel's. */
  r: 25 * CANVAS_FIGMA_SCALE,
  /** A brand logo's box inside the plate. The frame's marks are each their own (Claude 25, Gemini 28.1, Gmail a
   *  padded 40 raster), so a logo of ours takes one box near theirs. */
  logo: 26 * CANVAS_FIGMA_SCALE,
  /** The Wix wordmark, at the frame's own size. */
  wix: { w: 28.25 * CANVAS_FIGMA_SCALE, h: 10.937 * CANVAS_FIGMA_SCALE },
  /** The "+" plate's hairline (the frame's Border/Default at 1). */
  plusBorder: 1 * CANVAS_FIGMA_SCALE,
  /** The "+" plate's 16px lucide Plus (stroke 1.5 at 16, in the icon's 24 units). */
  plus: 16 * CANVAS_FIGMA_SCALE,
  plusStroke: (1.5 * 24) / 16,
} as const;

/** The connectors moved into the canvas row, in their shared order. Other regions pass through. */
export function placeCanvasConnectors(nodes: MapNode[]): MapNode[] {
  const row = nodes.filter((n) => n.region === 'connectors');
  const first = -((row.length - 1) * CANVAS_CONNECTORS.step) / 2;
  const at = new Map(row.map((n, i) => [n.id, first + i * CANVAS_CONNECTORS.step]));
  return nodes.map((node) =>
    node.region === 'connectors'
      ? { ...node, x: WORLD_CENTER.x + CANVAS_CONNECTORS.centre.dx + (at.get(node.id) ?? 0), y: WORLD_CENTER.y + CANVAS_CONNECTORS.centre.dy }
      : node,
  );
}

// ---------------------------------------------------------------------------------------------------------
// The canvas CAMERA — its own default, zoom limits and bounds (the shared `map-camera.ts` is mirrored 1:1
// by both phones and stays untouched)
// ---------------------------------------------------------------------------------------------------------

/** The canvas's 100% against the frame's own scale: a tenth smaller, so the graph sits smaller in the page
 *  than Figma draws it. */
const CANVAS_BASE_SCALE = 0.9;

/**
 * The canvas widths the two frames are drawn for — the main viewport of the 1440 frame (Figma 2043:99017,
 * 1180 wide) and of the 1920 one (2038:96146, 1660) — and how far a canvas is between them, 0–1, held at
 * either end. Every size that differs between the two frames is the 1440 one at 0, the 1920 one at 1, and
 * in proportion between (the CSS reads the same fraction as `--canvas-t`, set by `Home3Body`).
 */
export const CANVAS_NARROW_W = 1180;
export const CANVAS_WIDE_W = 1660;
export function canvasWidthT(width: number): number {
  return Math.min(1, Math.max(0, (width - CANVAS_NARROW_W) / (CANVAS_WIDE_W - CANVAS_NARROW_W)));
}
/** The 1440 frame's octopus against the 1920 one's: its ring, 256.21 over 335.31 — the whole graph, evenly. */
const CANVAS_NARROW_SCALE = 256.21 / 335.31;

/**
 * Screen px per world unit at the canvas's 100%, for a canvas this wide: the frame's own scale (its ring over the
 * world's) less a tenth, and on a 1440 canvas the 1440 frame's — the whole octopus smaller, evenly — in
 * proportion between the two.
 */
export function canvasPxPerUnit(width: number): number {
  const t = canvasWidthT(width);
  return (CANVAS_BASE_SCALE * (CANVAS_NARROW_SCALE + (1 - CANVAS_NARROW_SCALE) * t)) / CANVAS_FIGMA_SCALE;
}
/**
 * The canvas's ground (Figma 2038:96147's pattern fill): a 2px dot every 48 across and 42 down, as the frame
 * renders it — sized for the SCREEN at the canvas's 100% (on any width), in world units, so it scales with the
 * zoom. Its grid sits on the Maestro as the frame's does: a dot 37.66 right of it and 12.46 under it.
 */
export function canvasGround(width: number): { pitchX: number; pitchY: number; r: number; dx: number; dy: number } {
  const unit = 1 / canvasPxPerUnit(width);
  return { pitchX: 48 * unit, pitchY: 42 * unit, r: 1 * unit, dx: 37.66 * unit, dy: 12.46 * unit };
}
/** The zoom range, as fractions of that 100%. */
export const CANVAS_MIN_ZOOM = 0.3;
export const CANVAS_MAX_ZOOM = 2;

/** The entry lines' dots sit this far short of the entry column's end edge (1652 against 1717.65 on the frame). */
export const CANVAS_ENTRY_DOT_FROM_END = 65.65;

/**
 * The entry column's top END corner in the world, from the Maestro: the frame's 1717.65 · 223 against the
 * Maestro's 1234.84 · 453.04. The column is part of the graph — it pans and zooms with the camera (`HomeMap`
 * lays it out at the frame's size and scales it), so its lines keep the frame's length at every zoom. The END
 * corner, not the start: the column is as wide as its widest label, so its start moves with the locale.
 */
export const CANVAS_DOCK_ANCHOR = atFigma(1717.65, 223);

/** A camera rectangle in world units — the shape `map-camera.ts` uses. */
export interface CanvasView {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** The canvas host's size. */
export type CanvasViewport = { w: number; h: number };
type Viewport = CanvasViewport;

/** A box in the world. */
export interface CanvasBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The whole octopus's box in the world: the channel plates, the agents' orbit (all of it, so the box holds
 * still as the ring turns), the Maestro, the tools row, and the entry column — its laid-out size in the
 * frame's px (`column`, measured; `null` before it is), hanging from `CANVAS_DOCK_ANCHOR`.
 */
export function canvasGraphBounds(nodes: MapNode[], column: { w: number; h: number } | null): CanvasBounds {
  const ring = CANVAS_AGENTS.orbit + CANVAS_AGENTS.r;
  const b = { minX: WORLD_CENTER.x - ring, minY: WORLD_CENTER.y - ring, maxX: WORLD_CENTER.x + ring, maxY: WORLD_CENTER.y + ring };
  const grow = (x: number, y: number, r: number) => {
    b.minX = Math.min(b.minX, x - r);
    b.minY = Math.min(b.minY, y - r);
    b.maxX = Math.max(b.maxX, x + r);
    b.maxY = Math.max(b.maxY, y + r);
  };
  for (const node of nodes) {
    if (node.region === 'channelsIn') grow(node.x, node.y, CANVAS_CHANNEL.r);
    else if (node.region === 'connectors') grow(node.x, node.y, CANVAS_CONNECTORS.r);
  }
  if (column) {
    const end = { x: WORLD_CENTER.x + CANVAS_DOCK_ANCHOR.dx, y: WORLD_CENTER.y + CANVAS_DOCK_ANCHOR.dy };
    grow(end.x, end.y, 0);
    grow(end.x - column.w * CANVAS_FIGMA_SCALE, end.y + column.h * CANVAS_FIGMA_SCALE, 0);
  }
  return b;
}

/** The current zoom, as a fraction of the canvas's 100%. */
export function canvasZoom(v: CanvasView, viewport: Viewport): number {
  return viewport.w / Math.max(v.w, 1) / canvasPxPerUnit(viewport.w);
}

/**
 * The canvas's bounds: the camera may go anywhere that still has the world's CENTRE in view, so the graph can
 * be dragged around but never lost off-screen. (The shared `clamp` instead centres the world whenever the view
 * is bigger than it — which, at 100% on a desktop, is always, and would pin the canvas in place.) The entry
 * points move with the graph, so nothing between the two can be squeezed and there is no other bound.
 */
export function canvasClamp(v: CanvasView): CanvasView {
  const x = Math.min(Math.max(v.x, WORLD_CENTER.x - v.w), WORLD_CENTER.x);
  const y = Math.min(Math.max(v.y, WORLD_CENTER.y - v.h), WORLD_CENTER.y);
  return { x, y, w: v.w, h: v.h };
}

/**
 * The default view: 100%, with the Maestro on `centre.x` — the chat bar's centre, which the frame draws right
 * under it — and the octopus (`canvasGraphBounds`) centred down on `centre.y`, the middle of the canvas between
 * the header and the chat bar. Both in host px, measured by `HomeMap`.
 */
export function canvasDefaultView(viewport: Viewport, bounds: CanvasBounds, centre: { x: number; y: number }): CanvasView {
  const k = canvasPxPerUnit(viewport.w);
  const w = viewport.w / k;
  const h = viewport.h / k;
  const cy = (bounds.minY + bounds.maxY) / 2;
  return canvasClamp({ x: WORLD_CENTER.x - centre.x / k, y: cy - centre.y / k, w, h });
}

/** The zoom buttons' step: ten points of the canvas's 100%. */
const CANVAS_ZOOM_STEP = 0.1;

/**
 * The zoom a +/- press lands on from `zoom`: the next ten-point level up or down (100 → 110, 128 → 130 / 120),
 * held to 30%–200%. As a factor of the current zoom, for `canvasZoomAbout`.
 */
export function canvasZoomStep(zoom: number, dir: 1 | -1): number {
  const steps = zoom / CANVAS_ZOOM_STEP;
  const eps = 1e-6;
  const next = (dir > 0 ? Math.floor(steps + eps) + 1 : Math.ceil(steps - eps) - 1) * CANVAS_ZOOM_STEP;
  const held = Math.min(CANVAS_MAX_ZOOM, Math.max(CANVAS_MIN_ZOOM, next));
  return held / Math.max(zoom, eps);
}

/** Zoom by `factor` (>1 in) about a world point, held to 30%–200%, then bounded. */
export function canvasZoomAbout(v: CanvasView, factor: number, at: { x: number; y: number }, viewport: Viewport): CanvasView {
  const minW = viewport.w / (canvasPxPerUnit(viewport.w) * CANVAS_MAX_ZOOM);
  const maxW = viewport.w / (canvasPxPerUnit(viewport.w) * CANVAS_MIN_ZOOM);
  const w = Math.min(Math.max(v.w / factor, minW), maxW);
  const scale = w / v.w;
  return canvasClamp({ x: at.x - (at.x - v.x) * scale, y: at.y - (at.y - v.y) * scale, w, h: v.h * scale });
}

/** Pan by a screen delta, then bounded. */
export function canvasPan(v: CanvasView, dxPx: number, dyPx: number, viewport: Viewport): CanvasView {
  const unitsPerPx = v.w / Math.max(viewport.w, 1);
  return canvasClamp({ ...v, x: v.x - dxPx * unitsPerPx, y: v.y - dyPx * unitsPerPx });
}

// ---------------------------------------------------------------------------------------------------------
// The canvas AGENTS RING (Figma 2038:96143)
// ---------------------------------------------------------------------------------------------------------

/**
 * The agents on the canvas: the shared three-band agent dot at the frame's 45.68px, on NO plate, at the
 * frame's 164.17px from the Maestro (the mean of the six it draws on the ring; a seventh sits off it) — a
 * touch inside the 167.655px ring, where the shared layout sits them exactly on it. The ring itself keeps
 * `AGENT_RING_R` (120 = the frame's 167.655 at 100%).
 */
export const CANVAS_AGENTS = {
  r: (45.68 / 2) * CANVAS_FIGMA_SCALE,
  orbit: 164.17 * CANVAS_FIGMA_SCALE,
} as const;

/** The agents moved onto the canvas's own orbit, each keeping its angle (the shared even spacing). */
export function placeCanvasAgents(nodes: MapNode[]): MapNode[] {
  return nodes.map((node) => {
    if (node.region !== 'agents') return node;
    const a = Math.atan2(node.y - WORLD_CENTER.y, node.x - WORLD_CENTER.x);
    return { ...node, x: WORLD_CENTER.x + CANVAS_AGENTS.orbit * Math.cos(a), y: WORLD_CENTER.y + CANVAS_AGENTS.orbit * Math.sin(a) };
  });
}

/**
 * The Maestro tile as the canvas frame draws it — 66.662px, radius 16, four 12.795 white shapes (rounded
 * squares on the leading diagonal, circles on the other), each shape's offset from the tile's top-left corner
 * as the frame places them. In world units. NOT the shared `MaestroMark`, which ports native's 52 grid and so
 * lands on other shapes and a rounder tile at this size.
 */
export const CANVAS_MAESTRO = {
  size: 66.662 * CANVAS_FIGMA_SCALE,
  radius: 16 * CANVAS_FIGMA_SCALE,
  shape: 12.795 * CANVAS_FIGMA_SCALE,
  squareRadius: 4 * CANVAS_FIGMA_SCALE,
  squares: [
    { x: 18.078 * CANVAS_FIGMA_SCALE, y: 18.083 * CANVAS_FIGMA_SCALE },
    { x: 35.134 * CANVAS_FIGMA_SCALE, y: 34.439 * CANVAS_FIGMA_SCALE },
  ],
  circles: [
    { x: 35.134 * CANVAS_FIGMA_SCALE, y: 18.083 * CANVAS_FIGMA_SCALE },
    { x: 18.078 * CANVAS_FIGMA_SCALE, y: 34.671 * CANVAS_FIGMA_SCALE },
  ],
} as const;

// ---------------------------------------------------------------------------------------------------------
// The canvas LINES and their DOTS (Figma 2038:96143) — every path below is the frame's own vector, lifted
// out of its layer box (rotation and stroke inset undone) into the frame's coordinates, then mapped onto the
// world with the ring as the ruler. Colours are the frame's, each on its design token (exact, or the nearest
// ramp step where the frame used an off-system value); an opacity is the frame's own layer opacity.
// ---------------------------------------------------------------------------------------------------------

/** A frame point → the world. */
function fromFrame(fx: number, fy: number): { x: number; y: number } {
  return { x: WORLD_CENTER.x + (fx - FRAME_MAESTRO.x) * CANVAS_FIGMA_SCALE, y: WORLD_CENTER.y + (fy - FRAME_MAESTRO.y) * CANVAS_FIGMA_SCALE };
}

const r3 = (n: number) => Math.round(n * 1000) / 1000;

/** `M p0 C c1 c2 p3 [C …]` from a flat list of points (one M, then cubics). */
function cubicPath(points: { x: number; y: number }[]): string {
  const [first, ...rest] = points;
  let d = `M ${r3(first.x)} ${r3(first.y)}`;
  for (let i = 0; i + 2 < rest.length; i += 3) {
    d += ` C ${r3(rest[i].x)} ${r3(rest[i].y)}, ${r3(rest[i + 1].x)} ${r3(rest[i + 1].y)}, ${r3(rest[i + 2].x)} ${r3(rest[i + 2].y)}`;
  }
  return d;
}

/** The channel lines (left), each from its dot to just outside the ring — the frame's own cubic, in FRAME px. */
const CHANNEL_LINES_FRAME: Record<string, { path: number[][]; color: string; opacity: number; cap: 'round' | 'butt'; dot: [number, number]; dotOpacity: number }> = {
  'phone-app': { path: [[888.607, 261.684], [984.732, 261.684], [1002.218, 375.135], [1083.45, 378.116]], color: 'var(--ds-accent-08)', opacity: 1, cap: 'round', dot: [891.68, 260.94], dotOpacity: 1 },
  'web': { path: [[931.831, 407.752], [1007.888, 400.052], [998.138, 425.646], [1059.684, 425.646]], color: 'var(--ds-orange-400)', opacity: 1, cap: 'round', dot: [943.42, 405], dotOpacity: 1 },
  'email-in': { path: [[805.142, 522.519], [974.374, 509.128], [966.193, 455.503], [1057.857, 455.503]], color: 'var(--ds-accent-07)', opacity: 0.32, cap: 'butt', dot: [816.15, 519.69], dotOpacity: 0.32 },
  'whatsapp': { path: [[988.471, 574.695], [1044.451, 574.695], [1023.123, 501.825], [1059.076, 501.825]], color: 'var(--ds-accent-12)', opacity: 0.55, cap: 'round', dot: [992.38, 574.24], dotOpacity: 0.55 },
  'sms-in': { path: [[945.523, 654.663], [1039.05, 654.663], [1010.905, 557.456], [1093.779, 557.456]], color: 'var(--ds-accent-04)', opacity: 1, cap: 'butt', dot: [946.22, 653.96], dotOpacity: 1 },
};

/**
 * The entry-point lines (right), from the ring's right side to each entry point's dot. `start` is the ring end in
 * FRAME px; the curve is its two handles as FRACTIONS of the line's horizontal span, so it keeps the frame's shape
 * at any span (exact at 100%, default position). `endDx`/`endDy` place the end on the entry: from its dot, and
 * from the entry's art centre — the frame's own small offsets. The frame draws these from the dot to the ring;
 * they are turned round here, so every line runs out of the map.
 */
const ENTRY_LINES_FRAME: Record<string, { start: [number, number]; c1: [number, number]; c2: [number, number]; endDx: number; endDy: number; color: string; opacity: number; dot: string; dotOpacity: number }> = {
  brief: { start: [1414.313, 421.438], c1: [0.15614, 0], c2: [0.28876, 0], endDx: 3, endDy: 1, color: 'var(--ds-accent-04)', opacity: 1, dot: 'var(--ds-accent-04)', dotOpacity: 1 },
  tasks: { start: [1416.45, 432.125], c1: [0.17816, 0], c2: [0.26533, 0], endDx: 3, endDy: -0.5, color: 'var(--ds-accent-10)', opacity: 1, dot: 'var(--ds-accent-10)', dotOpacity: 1 },
  routines: { start: [1416.45, 441.189], c1: [0.15258, 0.01924], c2: [0.34512, 0.01255], endDx: 3.5, endDy: 1, color: 'var(--ds-accent-12)', opacity: 1, dot: 'var(--ds-accent-12)', dotOpacity: 1 },
  goals: { start: [1416.45, 452.431], c1: [0.1508, 0.00797], c2: [0.34289, -0.01225], endDx: 4, endDy: 4.5, color: 'var(--ds-red-300)', opacity: 1, dot: 'var(--ds-red-300)', dotOpacity: 1 },
  outreach: { start: [1416.45, 463.118], c1: [0.11885, 0], c2: [0.28735, 0], endDx: 3, endDy: -1, color: 'var(--ds-accent-08)', opacity: 1, dot: 'var(--ds-accent-08)', dotOpacity: 1 },
  apps: { start: [1414.313, 474.874], c1: [0.14946, 0], c2: [0.3592, 0], endDx: 3, endDy: 5, color: 'var(--ds-blue-300)', opacity: 1, dot: 'var(--ds-blue-300)', dotOpacity: 1 },
  files: { start: [1413, 486], c1: [0.1095, 0.02273], c2: [0.35725, 0], endDx: 3, endDy: -1, color: 'var(--ds-orange-400)', opacity: 1, dot: 'var(--ds-orange-400)', dotOpacity: 1 },
};

/** The one connectors line (bottom), from under the ring to the dot over the tools row — FRAME px. */
const CONNECTORS_LINE_FRAME = { path: [[1224.216, 635.186], [1277.127, 637.422], [1233.611, 723.126], [1274.892, 723.126]], dot: [1279.576, 723.92] as [number, number] };


/** A line dot: the frame's 12px circle — a 5 fill under a 1px page-grey ring (drawn as a 6 disc of the page
 *  surface under a 5 disc of the colour, so a translucent colour flattens over the page as the frame's does).
 *  The entry dots are that 12; the map's own dots are each line's `dotScale` of it. */
export const CANVAS_DOT = { ring: 6, fill: 5 } as const;

/** World units per px of `CANVAS_DOT` for the map's dots: the channel dots are the frame's 11.189, the
 *  connectors line's its 12.5 (a 12.622 x 12.379 oval). */
const CHANNEL_DOT_SCALE = (CANVAS_FIGMA_SCALE * 11.189) / 12;
const CONNECTORS_DOT_SCALE = (CANVAS_FIGMA_SCALE * 12.5) / 12;

export interface CanvasLine {
  id: string;
  d: string;
  /** The same line as its cubic chain's points (`M` then `C` triples), for whatever rides it. */
  points: { x: number; y: number }[];
  color: string;
  opacity: number;
  cap: 'round' | 'butt';
  dot: { x: number; y: number };
  dotColor: string;
  dotOpacity: number;
  /** World units per px of `CANVAS_DOT` — the dot's size at the frame's 100%. */
  dotScale: number;
  /** The line's end on the RING side — where a particle running it turns for the node it lands on. */
  ringEnd: { x: number; y: number };
}

/** The colour the frame does not have: a channel line for a channel the frame does not draw (an AI coder). */
const EXTRA_CHANNEL_COLOR = 'var(--ds-purple-200)';

/**
 * The channel lines for the channels this world actually holds — the frame's own line for each of its five
 * kinds, and for any other channel (an AI coder, the folded "+N") a line in the same language: out of the
 * plate's dot heading right, into the ring's arc heading right.
 */
export function canvasChannelLines(nodes: MapNode[]): CanvasLine[] {
  const lines: CanvasLine[] = [];
  for (const node of nodes) {
    if (node.region !== 'channelsIn') continue;
    const frame = !node.folded && node.slug ? CHANNEL_LINES_FRAME[node.slug] : undefined;
    if (frame) {
      const pts = frame.path.map(([x, y]) => fromFrame(x, y));
      lines.push({
        id: node.id,
        d: cubicPath(pts),
        points: pts,
        color: frame.color,
        opacity: frame.opacity,
        cap: frame.cap,
        dot: fromFrame(frame.dot[0], frame.dot[1]),
        dotColor: frame.color,
        dotOpacity: frame.dotOpacity,
        dotScale: CHANNEL_DOT_SCALE,
        ringEnd: pts[pts.length - 1],
      });
      continue;
    }
    // The plate's dot sits on its inline-end edge, a touch low — the frame's five average (+23.35, +3.07).
    const dot = { x: node.x + 23.35 * CANVAS_FIGMA_SCALE, y: node.y + 3.07 * CANVAS_FIGMA_SCALE };
    // Just outside the ring, as the frame's own lines end.
    const R = 177 * CANVAS_FIGMA_SCALE;
    const dy = Math.max(-R + 1, Math.min(R - 1, (dot.y - WORLD_CENTER.y) * 0.55));
    const end = { x: WORLD_CENTER.x - Math.sqrt(R * R - dy * dy), y: WORLD_CENTER.y + dy };
    const span = end.x - dot.x;
    const extraPts = [dot, { x: dot.x + span * 0.5, y: dot.y }, { x: end.x - span * 0.35, y: end.y }, end];
    lines.push({
      id: node.id,
      d: cubicPath(extraPts),
      points: extraPts,
      color: EXTRA_CHANNEL_COLOR,
      opacity: 1,
      cap: 'round',
      dot,
      dotColor: EXTRA_CHANNEL_COLOR,
      dotOpacity: 1,
      dotScale: CHANNEL_DOT_SCALE,
      ringEnd: end,
    });
  }
  return lines;
}

/** The connectors line — one, from under the ring to the dot over the tools row. */
export const CANVAS_CONNECTORS_LINE: CanvasLine = (() => {
  const pts = CONNECTORS_LINE_FRAME.path.map(([x, y]) => fromFrame(x, y));
  return {
    id: 'connectors',
    d: cubicPath(pts),
    points: pts,
    // The frame's Surface/Surface Card.
    color: 'var(--ds-surface-default-gray)',
    opacity: 1,
    cap: 'butt',
    dot: fromFrame(CONNECTORS_LINE_FRAME.dot[0], CONNECTORS_LINE_FRAME.dot[1]),
    dotColor: 'var(--ds-surface-default-gray)',
    dotOpacity: 1,
    dotScale: CONNECTORS_DOT_SCALE,
    ringEnd: pts[0],
  };
})();

/** An entry-point line, in its two coordinate systems: the ring end in the WORLD (it moves with the camera),
 *  the curve and the colour; the entry end is placed on screen by whoever measures the entry point. */
export interface CanvasEntryLine {
  entry: string;
  start: { x: number; y: number };
  c1: [number, number];
  c2: [number, number];
  /** The line's end, px from its entry's dot. */
  endDx: number;
  /** …and px from its entry's art centre. */
  endDy: number;
  color: string;
  opacity: number;
  dotColor: string;
  dotOpacity: number;
}

export const CANVAS_ENTRY_LINES: CanvasEntryLine[] = Object.entries(ENTRY_LINES_FRAME).map(([entry, l]) => ({
  entry,
  start: fromFrame(l.start[0], l.start[1]),
  c1: l.c1,
  c2: l.c2,
  endDx: l.endDx,
  endDy: l.endDy,
  color: l.color,
  opacity: l.opacity,
  dotColor: l.dot,
  dotOpacity: l.dotOpacity,
}));

/**
 * An entry-point line's path between two SCREEN points — its ring end projected through the camera, its
 * entry end measured on the entry point. The handles are the frame's, as fractions of the horizontal span,
 * so the line keeps its shape and leaves and lands level however far apart the two ends are dragged.
 */
export function canvasEntryPath(line: Pick<CanvasEntryLine, 'c1' | 'c2'>, p0: { x: number; y: number }, p3: { x: number; y: number }): string {
  return cubicPath(canvasEntryPoints(line, p0, p3));
}

/** The same line as its cubic's four points (`p0 c1 c2 p3`), for whatever rides it. */
export function canvasEntryPoints(line: Pick<CanvasEntryLine, 'c1' | 'c2'>, p0: Pt, p3: Pt): Pt[] {
  const span = Math.max(1, p3.x - p0.x);
  const c1 = { x: p0.x + line.c1[0] * span, y: p0.y + line.c1[1] * span };
  const c2 = { x: p3.x - line.c2[0] * span, y: p3.y - line.c2[1] * span };
  return [p0, c1, c2, p3];
}

type Pt = { x: number; y: number };

/**
 * A cubic chain (`M p0`, then `C` triples — the shape `cubicPath` draws) walked by DISTANCE: its length, and
 * the point that far along it. Sampled once into a polyline, so something can ride the line at an even speed
 * (a bézier's own parameter bunches up where the curve is tight).
 */
export function cubicChainWalker(points: Pt[], samplesPerCurve = 32): { length: number; at: (d: number) => Pt } {
  const pts: Pt[] = [points[0]];
  for (let i = 1; i + 2 < points.length; i += 3) {
    const [a, b, c, e] = [points[i - 1], points[i], points[i + 1], points[i + 2]];
    for (let k = 1; k <= samplesPerCurve; k++) {
      const t = k / samplesPerCurve;
      const u = 1 - t;
      pts.push({
        x: u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * c.x + t * t * t * e.x,
        y: u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * c.y + t * t * t * e.y,
      });
    }
  }
  const acc = [0];
  for (let i = 1; i < pts.length; i++) acc.push(acc[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  const length = acc[acc.length - 1];
  const at = (d: number): Pt => {
    const s = Math.max(0, Math.min(length, d));
    let lo = 0;
    let hi = acc.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (acc[mid] < s) lo = mid;
      else hi = mid;
    }
    const f = acc[hi] === acc[lo] ? 0 : (s - acc[lo]) / (acc[hi] - acc[lo]);
    return { x: pts[lo].x + (pts[hi].x - pts[lo].x) * f, y: pts[lo].y + (pts[hi].y - pts[lo].y) * f };
  };
  return { length, at };
}
